import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { 
  Users, 
  ThumbsUp, 
  ThumbsDown, 
  Bookmark, 
  BookmarkCheck,
  Trophy,
  TrendingUp,
  Clock,
  Eye,
  Filter,
  Search,
  Plus,
  CheckCircle,
  AlertCircle,
  Flame,
  Star,
  Award,
  Share2,
  Copy
} from 'lucide-react';

// Rating icons and colors
const RATING_CONFIG = {
  perfect: { icon: Flame, color: 'text-orange-500', bg: 'bg-orange-500/20', label: '🔥 Perfect' },
  good: { icon: CheckCircle, color: 'text-green-500', bg: 'bg-green-500/20', label: '✅ Good' },
  tweaked: { icon: AlertCircle, color: 'text-yellow-500', bg: 'bg-yellow-500/20', label: '⚠️ Needed Tweaking' },
  failed: { icon: AlertCircle, color: 'text-red-500', bg: 'bg-red-500/20', label: '❌ Failed' }
};

const DISCIPLINE_CONFIG = {
  cnc_routing: { label: 'CNC Routing', color: 'bg-amber-600' },
  '3d_printing': { label: '3D Printing', color: 'bg-orange-500' },
  laser_cutting: { label: 'Laser Cutting', color: 'bg-red-500' }
};

const CommunitySettings = ({ discipline = null }) => {
  const { user } = useContext(AuthContext);
  const token = localStorage.getItem('token');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [settings, setSettings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('browse');
  const [leaderboard, setLeaderboard] = useState([]);
  const [stats, setStats] = useState(null);
  const [savedSettings, setSavedSettings] = useState([]);
  const [mySettings, setMySettings] = useState([]);
  
  // Filters
  const [filters, setFilters] = useState({
    discipline: discipline || searchParams.get('discipline') || '',
    material: searchParams.get('material') || '',
    sort_by: 'score',
    min_success_rate: 0
  });
  
  // Share modal state
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareForm, setShareForm] = useState({
    title: '',
    description: '',
    result_rating: 'good',
    result_notes: '',
    tags: '',
    material: '',
    // CNC specific
    feed_rate: '',
    spindle_rpm: '',
    pass_depth: '',
    // 3D Print specific
    nozzle_temp: '',
    bed_temp: '',
    print_speed: '',
    // Laser specific
    power_percent: '',
    speed: '',
    passes: '',
    // Machine info
    machine_brand: '',
    machine_model: ''
  });
  
  // Fetch settings based on filters
  useEffect(() => {
    fetchSettings();
    fetchStats();
    fetchLeaderboard();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);
  
  // Fetch user's saved and own settings
  useEffect(() => {
    if (token) {
      fetchSavedSettings();
      fetchMySettings();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);
  
  const fetchSettings = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.discipline && filters.discipline !== 'all') params.append('discipline', filters.discipline);
      if (filters.material) params.append('material', filters.material);
      if (filters.sort_by) params.append('sort_by', filters.sort_by);
      if (filters.min_success_rate > 0) params.append('min_success_rate', filters.min_success_rate);
      
      const response = await axios.get(`${API}/community/settings?${params}`);
      setSettings(response.data);
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/community/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };
  
  const fetchLeaderboard = async () => {
    try {
      const params = (filters.discipline && filters.discipline !== 'all') ? `?discipline=${filters.discipline}` : '';
      const response = await axios.get(`${API}/community/leaderboard${params}`);
      setLeaderboard(response.data);
    } catch (error) {
      console.error('Failed to fetch leaderboard:', error);
    }
  };
  
  const fetchSavedSettings = async () => {
    try {
      const response = await axios.get(`${API}/community/settings/saved/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSavedSettings(response.data);
    } catch (error) {
      console.error('Failed to fetch saved settings:', error);
    }
  };
  
  const fetchMySettings = async () => {
    try {
      const response = await axios.get(`${API}/community/settings/my`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMySettings(response.data);
    } catch (error) {
      console.error('Failed to fetch my settings:', error);
    }
  };
  
  const handleVote = async (settingId, voteType, workedForMe) => {
    if (!token) {
      toast.error('Please log in to vote');
      return;
    }
    
    try {
      await axios.post(`${API}/community/settings/${settingId}/vote`, {
        vote_type: voteType,
        worked_for_me: workedForMe
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Vote recorded!');
      fetchSettings();
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error('You have already voted on this setting');
      } else {
        toast.error('Failed to vote');
      }
    }
  };
  
  const handleSave = async (settingId) => {
    if (!token) {
      toast.error('Please log in to save settings');
      return;
    }
    
    try {
      await axios.post(`${API}/community/settings/${settingId}/save`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Setting saved to your collection!');
      fetchSavedSettings();
      fetchSettings();
    } catch (error) {
      if (error.response?.status === 400) {
        // Already saved, try to unsave
        try {
          await axios.delete(`${API}/community/settings/${settingId}/save`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          toast.success('Removed from saved');
          fetchSavedSettings();
          fetchSettings();
        } catch (e) {
          toast.error('Failed to update');
        }
      } else {
        toast.error('Failed to save');
      }
    }
  };
  
  const handleShare = async () => {
    if (!token) {
      toast.error('Please log in to share settings');
      return;
    }
    
    // Build settings_data based on discipline
    const activeDiscipline = filters.discipline && filters.discipline !== 'all' ? filters.discipline : discipline || 'cnc_routing';
    let settings_data = { material: shareForm.material };
    
    if (activeDiscipline === 'cnc_routing') {
      if (shareForm.feed_rate) settings_data.feed_rate = parseFloat(shareForm.feed_rate);
      if (shareForm.spindle_rpm) settings_data.spindle_rpm = parseInt(shareForm.spindle_rpm);
      if (shareForm.pass_depth) settings_data.pass_depth = parseFloat(shareForm.pass_depth);
    } else if (activeDiscipline === '3d_printing') {
      if (shareForm.nozzle_temp) settings_data.nozzle_temp = parseInt(shareForm.nozzle_temp);
      if (shareForm.bed_temp) settings_data.bed_temp = parseInt(shareForm.bed_temp);
      if (shareForm.print_speed) settings_data.print_speed = parseInt(shareForm.print_speed);
    } else if (activeDiscipline === 'laser_cutting') {
      if (shareForm.power_percent) settings_data.power_percent = parseInt(shareForm.power_percent);
      if (shareForm.speed) settings_data.speed = parseFloat(shareForm.speed);
      if (shareForm.passes) settings_data.passes = parseInt(shareForm.passes);
    }
    
    try {
      const payload = {
        discipline: activeDiscipline,
        title: shareForm.title,
        description: shareForm.description,
        settings_data: settings_data,
        machine_info: shareForm.machine_brand ? {
          brand: shareForm.machine_brand,
          model: shareForm.machine_model
        } : null,
        result_rating: shareForm.result_rating,
        result_notes: shareForm.result_notes,
        tags: shareForm.tags.split(',').map(t => t.trim()).filter(t => t)
      };
      
      await axios.post(`${API}/community/settings`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Settings shared with the community! +5 reputation');
      setShowShareModal(false);
      setShareForm({
        title: '',
        description: '',
        result_rating: 'good',
        result_notes: '',
        tags: '',
        material: '',
        feed_rate: '',
        spindle_rpm: '',
        pass_depth: '',
        nozzle_temp: '',
        bed_temp: '',
        print_speed: '',
        power_percent: '',
        speed: '',
        passes: '',
        machine_brand: '',
        machine_model: ''
      });
      fetchSettings();
      fetchMySettings();
    } catch (error) {
      toast.error('Failed to share settings');
    }
  };
  
  const copySettingsToCalculator = (setting) => {
    // Store settings in localStorage for calculator to pick up
    localStorage.setItem('community_settings_import', JSON.stringify(setting.settings_data));
    
    // Navigate to appropriate calculator
    if (setting.discipline === 'cnc_routing') {
      navigate('/calculator?source=community');
    } else if (setting.discipline === '3d_printing') {
      navigate('/3d-printing/calculator?source=community');
    } else if (setting.discipline === 'laser_cutting') {
      navigate('/laser/calculator?source=community');
    }
    
    toast.success('Settings loaded! Open the calculator to use them.');
  };
  
  const SettingCard = ({ setting }) => {
    const ratingConfig = RATING_CONFIG[setting.result_rating] || RATING_CONFIG.good;
    const disciplineConfig = DISCIPLINE_CONFIG[setting.discipline];
    const isSaved = savedSettings.some(s => s.id === setting.id);
    
    return (
      <Card className="bg-black/70 backdrop-blur-sm border-zinc-700/50 hover:border-zinc-600 transition-all">
        <CardHeader className="pb-2">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <Badge className={disciplineConfig?.color || 'bg-zinc-600'}>
                  {disciplineConfig?.label || setting.discipline}
                </Badge>
                {setting.is_community_recommended && (
                  <Badge className="bg-gradient-to-r from-yellow-500 to-orange-500">
                    <Star className="w-3 h-3 mr-1" /> Community Pick
                  </Badge>
                )}
              </div>
              <CardTitle className="text-white text-lg">{setting.title}</CardTitle>
              <CardDescription className="text-zinc-400">
                by {setting.author_name} • {new Date(setting.created_at).toLocaleDateString()}
              </CardDescription>
            </div>
            <div className={`p-2 rounded-lg ${ratingConfig.bg}`}>
              <ratingConfig.icon className={`w-5 h-5 ${ratingConfig.color}`} />
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {setting.description && (
            <p className="text-sm text-zinc-300">{setting.description}</p>
          )}
          
          {/* Key settings preview */}
          <div className="grid grid-cols-2 gap-2 p-3 bg-zinc-800/50 rounded-lg">
            {setting.settings_data.material && (
              <div>
                <span className="text-xs text-zinc-500">Material</span>
                <p className="text-sm text-white">{setting.settings_data.material}</p>
              </div>
            )}
            {setting.settings_data.spindle_rpm && (
              <div>
                <span className="text-xs text-zinc-500">RPM</span>
                <p className="text-sm text-white">{setting.settings_data.spindle_rpm}</p>
              </div>
            )}
            {setting.settings_data.feed_rate && (
              <div>
                <span className="text-xs text-zinc-500">Feed Rate</span>
                <p className="text-sm text-white">{setting.settings_data.feed_rate} mm/min</p>
              </div>
            )}
            {setting.settings_data.nozzle_temp && (
              <div>
                <span className="text-xs text-zinc-500">Nozzle Temp</span>
                <p className="text-sm text-white">{setting.settings_data.nozzle_temp}°C</p>
              </div>
            )}
            {setting.settings_data.power_percent && (
              <div>
                <span className="text-xs text-zinc-500">Power</span>
                <p className="text-sm text-white">{setting.settings_data.power_percent}%</p>
              </div>
            )}
          </div>
          
          {/* Machine info */}
          {setting.machine_info?.brand && (
            <div className="text-xs text-zinc-500">
              Machine: {setting.machine_info.brand} {setting.machine_info.model}
            </div>
          )}
          
          {/* Tags */}
          {setting.tags?.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {setting.tags.map((tag, i) => (
                <Badge key={i} variant="outline" className="text-xs border-zinc-700">
                  {tag}
                </Badge>
              ))}
            </div>
          )}
          
          {/* Stats row */}
          <div className="flex items-center justify-between pt-2 border-t border-zinc-800">
            <div className="flex items-center gap-4 text-sm text-zinc-400">
              <span className="flex items-center gap-1">
                <TrendingUp className="w-4 h-4" />
                {setting.success_rate.toFixed(0)}% success
              </span>
              <span className="flex items-center gap-1">
                <Eye className="w-4 h-4" />
                {setting.view_count}
              </span>
            </div>
            
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleVote(setting.id, 'up', true)}
                className="text-green-400 hover:text-green-300 hover:bg-green-500/10"
              >
                <ThumbsUp className="w-4 h-4" />
                <span className="ml-1">{setting.upvotes}</span>
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleVote(setting.id, 'down', false)}
                className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
              >
                <ThumbsDown className="w-4 h-4" />
                <span className="ml-1">{setting.downvotes}</span>
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleSave(setting.id)}
                className={isSaved ? 'text-yellow-400' : 'text-zinc-400'}
              >
                {isSaved ? <BookmarkCheck className="w-4 h-4" /> : <Bookmark className="w-4 h-4" />}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => copySettingsToCalculator(setting)}
                className="text-orange-400 hover:text-orange-300"
              >
                <Copy className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };
  
  return (
    <div className="space-y-6" data-testid="community-settings">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs font-bold uppercase tracking-wider text-orange-500 mb-1">
            PROVEN SETTINGS
          </div>
          <h1 
            className="text-3xl md:text-4xl font-bold text-orange-500 tracking-tight uppercase"
            style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
          >
            Community Settings
          </h1>
          <p className="text-zinc-400 mt-1">
            Real settings from real makers. Rated by success.
          </p>
        </div>
        
        {token && (
          <Dialog open={showShareModal} onOpenChange={setShowShareModal}>
            <DialogTrigger asChild>
              <Button className="bg-orange-600 hover:bg-orange-500">
                <Plus className="w-4 h-4 mr-2" />
                Share Your Settings
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-zinc-900 border-zinc-700 max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="text-white">Share Proven Settings</DialogTitle>
                <DialogDescription className="text-zinc-400">
                  Share settings that worked for you and earn reputation!
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div>
                  <Label className="text-zinc-300">Title *</Label>
                  <Input
                    value={shareForm.title}
                    onChange={(e) => setShareForm({...shareForm, title: e.target.value})}
                    placeholder="e.g., Perfect Oak Pocketing - 1/4 inch End Mill"
                    className="bg-zinc-800 border-zinc-700 text-white mt-1"
                  />
                </div>
                <div>
                  <Label className="text-zinc-300">Description</Label>
                  <Textarea
                    value={shareForm.description}
                    onChange={(e) => setShareForm({...shareForm, description: e.target.value})}
                    placeholder="Describe when/why these settings work best..."
                    className="bg-zinc-800 border-zinc-700 text-white mt-1"
                  />
                </div>
                
                {/* Material field - common to all */}
                <div>
                  <Label className="text-zinc-300">Material *</Label>
                  <Input
                    value={shareForm.material}
                    onChange={(e) => setShareForm({...shareForm, material: e.target.value})}
                    placeholder="e.g., Oak, PLA, Plywood 3mm"
                    className="bg-zinc-800 border-zinc-700 text-white mt-1"
                  />
                </div>
                
                {/* Discipline-specific settings */}
                {(filters.discipline === 'cnc_routing' || filters.discipline === 'all' || !filters.discipline || discipline === 'cnc_routing') && (
                  <div className="p-3 bg-zinc-800/50 rounded-lg space-y-3">
                    <Label className="text-orange-400 text-sm font-semibold">CNC Settings</Label>
                    <div className="grid grid-cols-3 gap-2">
                      <div>
                        <Label className="text-xs text-zinc-400">Feed Rate (mm/min)</Label>
                        <Input
                          type="number"
                          value={shareForm.feed_rate}
                          onChange={(e) => setShareForm({...shareForm, feed_rate: e.target.value})}
                          placeholder="1500"
                          className="bg-zinc-800 border-zinc-700 text-white mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-zinc-400">Spindle RPM</Label>
                        <Input
                          type="number"
                          value={shareForm.spindle_rpm}
                          onChange={(e) => setShareForm({...shareForm, spindle_rpm: e.target.value})}
                          placeholder="18000"
                          className="bg-zinc-800 border-zinc-700 text-white mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-zinc-400">Pass Depth (mm)</Label>
                        <Input
                          type="number"
                          step="0.1"
                          value={shareForm.pass_depth}
                          onChange={(e) => setShareForm({...shareForm, pass_depth: e.target.value})}
                          placeholder="2.5"
                          className="bg-zinc-800 border-zinc-700 text-white mt-1"
                        />
                      </div>
                    </div>
                  </div>
                )}
                
                {(filters.discipline === '3d_printing' || discipline === '3d_printing') && (
                  <div className="p-3 bg-zinc-800/50 rounded-lg space-y-3">
                    <Label className="text-orange-400 text-sm font-semibold">3D Print Settings</Label>
                    <div className="grid grid-cols-3 gap-2">
                      <div>
                        <Label className="text-xs text-zinc-400">Nozzle Temp (°C)</Label>
                        <Input
                          type="number"
                          value={shareForm.nozzle_temp}
                          onChange={(e) => setShareForm({...shareForm, nozzle_temp: e.target.value})}
                          placeholder="210"
                          className="bg-zinc-800 border-zinc-700 text-white mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-zinc-400">Bed Temp (°C)</Label>
                        <Input
                          type="number"
                          value={shareForm.bed_temp}
                          onChange={(e) => setShareForm({...shareForm, bed_temp: e.target.value})}
                          placeholder="60"
                          className="bg-zinc-800 border-zinc-700 text-white mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-zinc-400">Print Speed (mm/s)</Label>
                        <Input
                          type="number"
                          value={shareForm.print_speed}
                          onChange={(e) => setShareForm({...shareForm, print_speed: e.target.value})}
                          placeholder="50"
                          className="bg-zinc-800 border-zinc-700 text-white mt-1"
                        />
                      </div>
                    </div>
                  </div>
                )}
                
                {(filters.discipline === 'laser_cutting' || discipline === 'laser_cutting') && (
                  <div className="p-3 bg-zinc-800/50 rounded-lg space-y-3">
                    <Label className="text-orange-400 text-sm font-semibold">Laser Settings</Label>
                    <div className="grid grid-cols-3 gap-2">
                      <div>
                        <Label className="text-xs text-zinc-400">Power (%)</Label>
                        <Input
                          type="number"
                          value={shareForm.power_percent}
                          onChange={(e) => setShareForm({...shareForm, power_percent: e.target.value})}
                          placeholder="80"
                          className="bg-zinc-800 border-zinc-700 text-white mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-zinc-400">Speed (mm/s)</Label>
                        <Input
                          type="number"
                          value={shareForm.speed}
                          onChange={(e) => setShareForm({...shareForm, speed: e.target.value})}
                          placeholder="20"
                          className="bg-zinc-800 border-zinc-700 text-white mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-zinc-400">Passes</Label>
                        <Input
                          type="number"
                          value={shareForm.passes}
                          onChange={(e) => setShareForm({...shareForm, passes: e.target.value})}
                          placeholder="1"
                          className="bg-zinc-800 border-zinc-700 text-white mt-1"
                        />
                      </div>
                    </div>
                  </div>
                )}
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-zinc-300">Result Rating *</Label>
                    <Select 
                      value={shareForm.result_rating}
                      onValueChange={(v) => setShareForm({...shareForm, result_rating: v})}
                    >
                      <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-800 border-zinc-700">
                        <SelectItem value="perfect">🔥 Perfect</SelectItem>
                        <SelectItem value="good">✅ Good</SelectItem>
                        <SelectItem value="tweaked">⚠️ Needed Tweaking</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-zinc-300">Machine Brand</Label>
                    <Input
                      value={shareForm.machine_brand}
                      onChange={(e) => setShareForm({...shareForm, machine_brand: e.target.value})}
                      placeholder="e.g., Shapeoko"
                      className="bg-zinc-800 border-zinc-700 text-white mt-1"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-zinc-300">Result Notes</Label>
                  <Textarea
                    value={shareForm.result_notes}
                    onChange={(e) => setShareForm({...shareForm, result_notes: e.target.value})}
                    placeholder="Any tips or observations from using these settings..."
                    className="bg-zinc-800 border-zinc-700 text-white mt-1"
                  />
                </div>
                <div>
                  <Label className="text-zinc-300">Tags (comma separated)</Label>
                  <Input
                    value={shareForm.tags}
                    onChange={(e) => setShareForm({...shareForm, tags: e.target.value})}
                    placeholder="e.g., hardwood, pocketing, finishing"
                    className="bg-zinc-800 border-zinc-700 text-white mt-1"
                  />
                </div>
                <Button 
                  onClick={handleShare}
                  className="w-full bg-orange-600 hover:bg-orange-500"
                  disabled={!shareForm.title}
                >
                  <Share2 className="w-4 h-4 mr-2" />
                  Share Settings (+5 Rep)
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>
      
      {/* Stats Banner */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-gradient-to-br from-orange-600/20 to-orange-900/20 border-orange-500/30">
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-orange-400">{stats.total_settings}</div>
              <div className="text-xs text-zinc-400">Shared Settings</div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-green-600/20 to-green-900/20 border-green-500/30">
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-green-400">{stats.total_votes}</div>
              <div className="text-xs text-zinc-400">Community Votes</div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-blue-600/20 to-blue-900/20 border-blue-500/30">
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-blue-400">{leaderboard.length}</div>
              <div className="text-xs text-zinc-400">Contributors</div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-purple-600/20 to-purple-900/20 border-purple-500/30">
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-purple-400">
                {settings.filter(s => s.is_community_recommended).length}
              </div>
              <div className="text-xs text-zinc-400">Community Picks</div>
            </CardContent>
          </Card>
        </div>
      )}
      
      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Filters Sidebar */}
        <Card className="lg:col-span-1 bg-black/70 backdrop-blur-sm border-zinc-700/50 h-fit">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Filter className="w-5 h-5 text-orange-500" />
              Filters
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-zinc-400 mb-2 block">Discipline</Label>
              <div className="px-3 py-2 rounded-md bg-zinc-800 border border-zinc-700 text-white text-sm">
                {DISCIPLINE_CONFIG[discipline]?.label || 'CNC Routing'}
              </div>
            </div>
            
            <div>
              <Label className="text-zinc-400 mb-2 block">Material</Label>
              <Input
                value={filters.material}
                onChange={(e) => setFilters({...filters, material: e.target.value})}
                placeholder="Search material..."
                className="bg-zinc-800 border-zinc-700 text-white"
              />
            </div>
            
            <div>
              <Label className="text-zinc-400 mb-2 block">Sort By</Label>
              <Select 
                value={filters.sort_by}
                onValueChange={(v) => setFilters({...filters, sort_by: v})}
              >
                <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-700">
                  <SelectItem value="score">Top Rated</SelectItem>
                  <SelectItem value="recent">Most Recent</SelectItem>
                  <SelectItem value="success_rate">Highest Success</SelectItem>
                  <SelectItem value="views">Most Viewed</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label className="text-zinc-400 mb-2 block">Min Success Rate</Label>
              <Select 
                value={String(filters.min_success_rate)}
                onValueChange={(v) => setFilters({...filters, min_success_rate: parseInt(v)})}
              >
                <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-700">
                  <SelectItem value="0">Any</SelectItem>
                  <SelectItem value="50">50%+</SelectItem>
                  <SelectItem value="70">70%+</SelectItem>
                  <SelectItem value="80">80%+</SelectItem>
                  <SelectItem value="90">90%+</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Leaderboard */}
            <div className="pt-4 border-t border-zinc-800">
              <div className="flex items-center gap-2 mb-3">
                <Trophy className="w-4 h-4 text-yellow-500" />
                <span className="text-sm font-medium text-white">Top Contributors</span>
              </div>
              <div className="space-y-2">
                {leaderboard.slice(0, 5).map((leader, i) => (
                  <div key={leader.author_id} className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">
                      <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold ${
                        i === 0 ? 'bg-yellow-500 text-black' :
                        i === 1 ? 'bg-zinc-400 text-black' :
                        i === 2 ? 'bg-orange-600 text-white' :
                        'bg-zinc-700 text-white'
                      }`}>
                        {i + 1}
                      </span>
                      <span className="text-zinc-300 truncate max-w-[100px]">{leader.author_name}</span>
                    </div>
                    <span className="text-zinc-500">{leader.total_upvotes}⬆</span>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
        
        {/* Settings List */}
        <div className="lg:col-span-3">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="bg-zinc-800 border-zinc-700 mb-4">
              <TabsTrigger value="browse" className="data-[state=active]:bg-orange-600">
                Browse All
              </TabsTrigger>
              {token && (
                <>
                  <TabsTrigger value="saved" className="data-[state=active]:bg-orange-600">
                    Saved ({savedSettings.length})
                  </TabsTrigger>
                  <TabsTrigger value="mine" className="data-[state=active]:bg-orange-600">
                    My Settings ({mySettings.length})
                  </TabsTrigger>
                </>
              )}
            </TabsList>
            
            <TabsContent value="browse" className="space-y-4">
              {loading ? (
                <div className="text-center py-12 text-zinc-400">
                  <Users className="w-12 h-12 mx-auto mb-4 animate-pulse" />
                  <p>Loading community settings...</p>
                </div>
              ) : settings.length === 0 ? (
                <Card className="bg-black/70 border-zinc-700/50">
                  <CardContent className="text-center py-12">
                    <Users className="w-12 h-12 mx-auto mb-4 text-zinc-600" />
                    <h3 className="text-lg text-zinc-400 mb-2">No settings found</h3>
                    <p className="text-zinc-500">Be the first to share settings for this filter!</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {settings.map(setting => (
                    <SettingCard key={setting.id} setting={setting} />
                  ))}
                </div>
              )}
            </TabsContent>
            
            <TabsContent value="saved" className="space-y-4">
              {savedSettings.length === 0 ? (
                <Card className="bg-black/70 border-zinc-700/50">
                  <CardContent className="text-center py-12">
                    <Bookmark className="w-12 h-12 mx-auto mb-4 text-zinc-600" />
                    <h3 className="text-lg text-zinc-400 mb-2">No saved settings</h3>
                    <p className="text-zinc-500">Save settings you find useful for quick access!</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {savedSettings.map(setting => (
                    <SettingCard key={setting.id} setting={setting} />
                  ))}
                </div>
              )}
            </TabsContent>
            
            <TabsContent value="mine" className="space-y-4">
              {mySettings.length === 0 ? (
                <Card className="bg-black/70 border-zinc-700/50">
                  <CardContent className="text-center py-12">
                    <Share2 className="w-12 h-12 mx-auto mb-4 text-zinc-600" />
                    <h3 className="text-lg text-zinc-400 mb-2">You haven't shared any settings</h3>
                    <p className="text-zinc-500 mb-4">Share your proven settings and earn reputation!</p>
                    <Button onClick={() => setShowShareModal(true)} className="bg-orange-600 hover:bg-orange-500">
                      <Plus className="w-4 h-4 mr-2" />
                      Share Settings
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {mySettings.map(setting => (
                    <SettingCard key={setting.id} setting={setting} />
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
};

export default CommunitySettings;
